import React, { useMemo, useCallback, useEffect } from "react";
import {
  DamageSideMap,
  DamageSeverity,
  DamageSeverityMap,
  InspectionType,
  BOLMediaPurpose,
} from "../../utils/enums";
import { forEachDamageSection } from "../../utils/damageGrid";
import "./style.css";
import Header from "./Header";
import InfoGrid from "./InfoGrid";
import DriverNotes from "./DriverNotes";
import Photos from "./Photos";
import Inspection from "./Inspection";
import AdditionalComments from "./AdditionalComments";

const BOL = ({ vehicleData, inspectionType, isHistorical }) => {
  const pickupInspectionData = useMemo(() => {
    return (
      (vehicleData || []).filter(inspectionData => {
        return inspectionData.type === InspectionType.Pickup;
      })[0] ?? {}
    );
  }, [vehicleData]);

  const dropOffInspectionData = useMemo(() => {
    return (
      (vehicleData || []).filter(inspectionData => {
        return inspectionData.type === InspectionType.DropOff;
      })[0] ?? {}
    );
  }, [vehicleData]);

  const inspectionData = useMemo(() => {
    var data =
      inspectionType === InspectionType.Pickup
        ? pickupInspectionData
        : dropOffInspectionData;
    data.pickupContactName =
      pickupInspectionData.pickupContactName ||
      pickupInspectionData.receiverName;
    data.dropoffContactName =
      dropOffInspectionData.dropOffContactName ||
      dropOffInspectionData.receiverName;
    data.isDropOffPending =
      !isHistorical &&
      ((dropOffInspectionData &&
        Object.keys(dropOffInspectionData).length === 0) ||
        !dropOffInspectionData?.completedAt);
    return data;
  }, [
    inspectionType,
    pickupInspectionData,
    dropOffInspectionData,
    isHistorical,
  ]);

  const setDiagramsColors = useCallback(() => {
    const mostDamage = {};
    const { damages = [] } = inspectionData ?? { damages: [] };

    // A section shows its most severe damage. Dropoff sections can hold both
    // the damage carried over from pickup and one added in transit.
    for (const damage of damages || []) {
      if (mostDamage[damage.side] === undefined) {
        mostDamage[damage.side] = {};
      }

      if (mostDamage[damage.side][damage.row] === undefined) {
        mostDamage[damage.side][damage.row] = {};
      }

      const value = mostDamage[damage.side][damage.row][damage.column] ?? 0;
      mostDamage[damage.side][damage.row][damage.column] = Math.max(
        value,
        damage.severity,
      );
    }

    forEachDamageSection((side, row, column) => {
      const sideName = DamageSideMap[side].toLowerCase();
      const element = document.querySelector(`.${sideName}-${row}-${column}`);

      if (!element) {
        return;
      }

      // These styles are set imperatively, so every section has to be repainted
      // on each pass or the previous inspection's colors linger when the
      // Pickup/Dropoff tab changes.
      const severity = mostDamage[side]?.[row]?.[column] ?? DamageSeverity.None;
      const colorName = (
        DamageSeverityMap[severity] ?? DamageSeverityMap[DamageSeverity.None]
      ).toLowerCase();

      element.style.backgroundColor = `var(--${colorName})`;
    });
  }, [inspectionData]);

  const contactSignatures = useMemo(() => {
    // If the drop off is pending, return null for both signatures
    if (inspectionData?.isDropOffPending) {
      return {
        [InspectionType.Pickup]: null,
        [InspectionType.DropOff]: null,
      };
    }

    const pickupSignature =
      (pickupInspectionData?.medias || []).find(
        media => media.purpose === BOLMediaPurpose.Signatures,
      )?.url ?? null;

    const dropOffSignature =
      (dropOffInspectionData?.medias || []).find(
        media => media.purpose === BOLMediaPurpose.Signatures,
      )?.url ?? null;

    return {
      [InspectionType.Pickup]: pickupSignature,
      [InspectionType.DropOff]: dropOffSignature,
    };
  }, [
    dropOffInspectionData,
    pickupInspectionData,
    inspectionData?.isDropOffPending,
  ]);

  useEffect(() => {
    setDiagramsColors();
  }, [setDiagramsColors]);

  return (
    <div id="pdf-doc">
      <Header inspectionData={inspectionData} inspectionType={inspectionType} />
      <InfoGrid
        inspectionData={inspectionData}
        placeInspectionData={{
          [InspectionType.Pickup]: pickupInspectionData,
          [InspectionType.DropOff]: dropOffInspectionData,
        }}
        contactSignatures={contactSignatures}
        inspectionType={inspectionType}
        isHistorical={isHistorical}
      />
      <DriverNotes
        inspectionType={inspectionType}
        notes={inspectionData.note}
      />
      <Photos inspectionType={inspectionType} photos={inspectionData.medias} />

      <Inspection
        inspectionType={inspectionType}
        inspectionData={inspectionData}
        pickupDamages={pickupInspectionData.damages}
      />

      <AdditionalComments
        inspectionType={inspectionType}
        comments={inspectionData.additionalComments}
      />
    </div>
  );
};

export default BOL;
